/**
 * BillingAlertBanner — red strip on /coach when there are unacknowledged
 * `invoice.payment_failed` events. Coach/owner taps Acknowledge to dismiss
 * (server-side); a fresh payment_succeeded auto-resolves the same alert.
 *
 * Polls every 60s so a new card failure shows up without a page refresh.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, Check, Loader2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingAlertBanner() {
    const [alerts, setAlerts] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [ackingId, setAckingId] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/coach/billing/alerts`, { withCredentials: true });
            setAlerts(r.data?.alerts || []);
        } catch {
            // silent — banner is non-critical
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, [load]);

    const acknowledge = async (alert_id) => {
        setAckingId(alert_id);
        try {
            await axios.post(
                `${API}/coach/billing/alerts/${alert_id}/acknowledge`,
                {},
                { withCredentials: true },
            );
            toast.success("Got it — alert acknowledged");
            await load();
        } catch (e) {
            toast.error("Couldn't acknowledge", { description: e?.response?.data?.detail });
        } finally {
            setAckingId(null);
        }
    };

    if (loading || alerts.length === 0) return null;

    const total = alerts.length;
    const headline = total === 1
        ? `Payment failed — ${alerts[0].user_name || alerts[0].user_email || "a member"}`
        : `${total} payments failed — needs review`;

    return (
        <div
            data-testid="billing-alert-banner"
            className="border border-brand-red bg-brand-red/10 rounded-xl overflow-hidden mt-2"
        >
            <button
                type="button"
                data-testid="billing-alert-toggle"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-brand-red/15 transition-colors"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <AlertTriangle className="w-5 h-5 text-brand-red shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">
                            Billing alert · {total} open
                        </div>
                        <div className="text-sm text-white truncate">{headline}</div>
                    </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
            </button>

            {expanded && (
                <ul className="divide-y divide-brand-red/40 border-t border-brand-red/40">
                    {alerts.map((a) => {
                        const amount = (a.amount_cents || 0) / 100;
                        const amountStr = `$${amount.toFixed(2)}`;
                        const nextAttempt = a.next_attempt_at
                            ? new Date(a.next_attempt_at).toLocaleString(undefined, {
                                weekday: "short", month: "short", day: "numeric",
                                hour: "numeric", minute: "2-digit",
                            })
                            : "final attempt — sub will pause";
                        return (
                            <li
                                key={a.alert_id}
                                data-testid={`billing-alert-row-${a.alert_id}`}
                                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-black/40"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-white truncate">
                                        {a.user_name || "—"}
                                        <span className="ml-2 text-[11px] text-zinc-400">{a.user_email}</span>
                                    </div>
                                    <div className="text-[11px] text-zinc-300 mt-0.5">
                                        {amountStr}
                                        {a.plan_name && <> · {a.plan_name}</>}
                                        {a.card_last4 && <> · •••• {a.card_last4}</>}
                                        {` · attempt ${a.attempt_count || 1} · next: ${nextAttempt}`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {a.hosted_invoice_url && (
                                        <a
                                            href={a.hosted_invoice_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`billing-alert-invoice-${a.alert_id}`}
                                            className="text-[11px] tracking-[0.15em] uppercase px-3 py-2 border border-zinc-700 text-zinc-200 hover:border-zinc-400 inline-flex items-center gap-1.5"
                                        >
                                            <ExternalLink className="w-3 h-3" /> Stripe
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`billing-alert-ack-${a.alert_id}`}
                                        onClick={() => acknowledge(a.alert_id)}
                                        disabled={ackingId === a.alert_id}
                                        className="text-[11px] tracking-[0.15em] uppercase px-3 py-2 bg-brand-red text-white hover:bg-brand-red/90 inline-flex items-center gap-1.5 disabled:opacity-50"
                                    >
                                        {ackingId === a.alert_id
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <Check className="w-3 h-3" />}
                                        Acknowledge
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
